import APIResponse from "src/types/response.type";
import axios from "../core/axios";
import type { ChannelMedia, channelSchema } from "../types/ChannelsResponse";
import type { TestChannelRequest, TestChannelResponse } from "../types/ChannelTestTypes";

export default class channelsService {
  async getChannelsMetadata(): Promise<ChannelMedia[]> {
    const response = await axios.get("/metadata");

    if (response.status === 200) {
      return response.data.data; // Ensure this matches ChannelMediaResponse
    } else {
      throw new Error("Unable to fetch Channels MetaData");
    }
  }

  ///// get channel schema

  async getChannelSchema(channelName: string): Promise<APIResponse<channelSchema>> {
    const response = await axios.get(`/metadata/${channelName}`, {
      params: {
        channelName: channelName,
      },
    });

    if (response.status === 200) {
      return response.data.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to fetch channel schema";
    }
  }

  ///// add channel

  async addChannel(data: any): Promise<any> {
    const response = await axios.post(`/channels`, data);

    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to add channel ";
    }
  }

  ///// update channel

  async updateChannel(id: string, data: any): Promise<any> {
    const response = await axios.put(`/channels/${id}`, data);

    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to update channel ";
    }
  }

  ///// delete channel

  async deleteChannel(id: string): Promise<any> {
    const response = await axios.delete(`/channels/${id}`);

    if (response.status === 200 || response.status === 204) {
      return response.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to delete channel ";
    }
  }

  ///// get all channels
  async getAllChannels(
    limit: number,
    page: number,
    searchQuery: string,
    channelType: string, // Keep for backward compatibility
    channelTypes?: string[], // Add new parameter for multiple types
  ) {
    const params: any = {
      limit: limit,
      page: page,
      searchQuery: searchQuery,
    };

    // Handle multiple channel types (new functionality)
    if (channelTypes && channelTypes.length > 0) {
      params.channelTypes = channelTypes.join(',');
    }
    // Fallback to single channel type (backward compatibility)
    else if (channelType) {
      params.channelType = channelType;
    }

    const response = await axios.get("/channels", { params });

    if (response.status === 200) {
      return response.data.data;
    } else {
      throw new Error("Unable to fetch channels");
    }
  }

  //// get channel by id

  async getChannel(id: string) {
    const response = await axios.get(`/channels/${id}`);

    if (response.status === 200) {
      return response.data.data;
    } else {
      throw new Error("Unable to fetch channel");
    }
  }

  // WhatsApp Cloud onboarding helpers
  async registerWhatsApp(id: string, payload: {
    phoneNumberId: string;
    wabaId: string;
    accessToken: string;
    appId?: string;
    appSecret?: string;
    verifyToken: string;
    displayPhoneNumber?: string;
  }) {
    const response = await axios.post(`/channels/${id}/whatsapp/register`, payload);
    return response.data;
  }

  async listWhatsAppNumbers(id: string) {
    const response = await axios.get(`/channels/${id}/whatsapp/phone-numbers`);
    return response.data;
  }

  async requestWhatsAppCode(id: string, phoneNumberId: string, method: 'SMS' | 'VOICE' = 'SMS', language = 'en_US') {
    const response = await axios.post(`/channels/${id}/whatsapp/phone-numbers/${phoneNumberId}/request-code`, { method, language });
    return response.data;
  }

  async verifyWhatsAppCode(id: string, phoneNumberId: string, code: string) {
    const response = await axios.post(`/channels/${id}/whatsapp/phone-numbers/${phoneNumberId}/verify-code`, { code });
    return response.data;
  }

  async subscribeWhatsAppApp(id: string) {
    const response = await axios.post(`/channels/${id}/whatsapp/subscribe-app`);
    return response.data;
  }

  async getChannelUsage(channelId: string) {
    const response = await axios.get(`/billing/usage/channel/${channelId}`);
    return response.data;
  }

  /**
   * Test channel credentials by sending a test message.
   * POST /channels/test
   */
  async testChannel(request: TestChannelRequest): Promise<TestChannelResponse> {
    try {
      const response = await axios.post(`/channels/test`, request);
      // API wraps response in { status, message, data } - extract the actual test result
      return response.data.data as TestChannelResponse;
    } catch (error: any) {
      // For failed tests (HTTP 400), the test result is in error.response.data.data
      if (error?.response?.data?.data?.success !== undefined) {
        return error.response.data.data as TestChannelResponse;
      }
      // Fallback: check if result is directly in error.response.data
      if (error?.response?.data?.success !== undefined) {
        return error.response.data as TestChannelResponse;
      }
      // Otherwise construct an error response
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to test channel credentials',
        details: {
          errorCode: 'UNKNOWN_ERROR',
          provider: request.channelType?.toLowerCase(),
        },
      };
    }
  }
}
